import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, X, ImagePlus } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { PendingButton } from '../../../../../components/ui/PendingButton';
import { Modal } from '../../../../../components/ui/Modal';
import { ConfirmDialog } from '../../../../../components/ui/ConfirmDialog';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { DELIVERY_STATUSES, STATUS_MAP } from '../../../../../utils/catalogs';
import { clearProofImages } from '../../../../../features/orderDetails/orderDetailsSlice';
import {
  deleteProofOfDelivery,
  updateOrderState,
  uploadProofOfDelivery,
} from '../../../../../features/orderDetails/orderDetailsThunks';
import { useProofPreviews } from '../../../../../features/orderDetails/useProofPreviews';
import { selectIsRunning } from '../../../../../features/feedback/feedbackSlice';

const MAX_PROOF_IMAGES = 3;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

const STATUS_OPTIONS = DELIVERY_STATUSES.map((status) => ({
  value: status.system_name,
  label: status.user_name,
}));

/**
 * Editor for the delivery status of an order.
 *
 * The substatus list is derived from the chosen status through the shared
 * catalog, so only combinations the platform recognises can be submitted —
 * "Entregado / Embalaje dañado" is simply not offerable. That replaces the
 * legacy prefix matching over a flat list, which needed a second translation
 * table to turn `delivered_complete` back into the `complete` the backend
 * stores; here the catalog already holds the stored names.
 *
 * Proof-of-delivery images are uploaded as they are picked rather than on save,
 * because the backend stores URLs, not files. Removing one deletes it remotely
 * too, so an abandoned edit leaves no orphans behind.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.order
 * @param {(message: string) => void} props.onError
 */
const EditStatusModal = ({ isOpen, onClose, order, onError }) => {
  const dispatch = useDispatch();
  const { proofImages, uploadingProof, proofError } = useSelector((state) => state.orderDetails);
  const removingProof = useSelector(selectIsRunning(deleteProofOfDelivery));
  // The real number of bultos, not `delivery.packages` — that field is derived
  // and goes stale, and this warning covers an irreversible action, so it must
  // not understate what it is about to touch.
  const { count: itemsCount } = useSelector((state) => state.deliveryItems);
  const { previewOf, remember, forget, forgetAll } = useProofPreviews();

  const [status, setStatus] = useState('');
  const [substatus, setSubstatus] = useState('');
  const [details, setDetails] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileError, setFileError] = useState('');

  const substatusOptions = useMemo(
    () =>
      (STATUS_MAP[status]?.substatus || []).map((option) => ({
        value: option.system_name,
        label: option.user_name,
      })),
    [status]
  );

  const reset = () => {
    setStatus('');
    setSubstatus('');
    setDetails('');
    setFileError('');
    setConfirming(false);
    dispatch(clearProofImages());
    forgetAll();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    // The previous substatus almost never hangs from the new status.
    setSubstatus('');
  };

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    // Cleared so picking the same file twice in a row still fires onChange.
    event.target.value = '';
    if (files.length === 0) return;

    setFileError('');

    if (files.some((file) => !ACCEPTED_TYPES.includes(file.type))) {
      setFileError('Solo se aceptan imágenes PNG y JPG.');
      return;
    }
    if (files.length + proofImages.length > MAX_PROOF_IMAGES) {
      setFileError(`Puedes adjuntar hasta ${MAX_PROOF_IMAGES} imágenes.`);
      return;
    }

    try {
      const urls = await dispatch(uploadProofOfDelivery({ orderId: order.id, files })).unwrap();
      remember(urls, files);
    } catch (error) {
      setFileError(error);
    }
  };

  const handleRemove = async (url) => {
    try {
      await dispatch(deleteProofOfDelivery(url)).unwrap();
      forget(url);
    } catch (error) {
      setFileError(error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await dispatch(
        updateOrderState({
          orderId: order.id,
          status,
          substatus,
          details,
          imageUrls: proofImages,
        })
      ).unwrap();
      reset();
      onClose();
    } catch (error) {
      setConfirming(false);
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  // Closing mid-upload would stage its images for the next opening, and saving
  // mid-change would send an image that is on its way in or out.
  const busy = saving || uploadingProof || removingProof;
  const canSave = Boolean(status && substatus) && !uploadingProof && !removingProof;

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} title="Editar Estado de Entrega" busy={busy}>
        <div className="flex flex-col gap-4 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5">
              Estado
            </label>
            <FilterableSelect
              variant="form"
              value={status}
              onChange={handleStatusChange}
              options={STATUS_OPTIONS}
              placeholder="Selecciona un estado"
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5">
              Subestado
            </label>
            <FilterableSelect
              variant="form"
              value={substatus}
              onChange={setSubstatus}
              options={substatusOptions}
              disabled={!status}
              placeholder={status ? 'Selecciona un subestado' : 'Elige antes el estado'}
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1.5">
              Detalle
            </label>
            <input
              type="text"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Observación visible en el historial"
              className="w-full bg-transparent border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-ninja-green transition-colors"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-wider text-text-muted">
                Evidencia (máximo {MAX_PROOF_IMAGES})
              </label>
              {uploadingProof && <Loader2 size={14} className="animate-spin text-ninja-green" />}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {proofImages.map((url) => (
                <div
                  key={url}
                  className="relative w-20 h-20 rounded-xl overflow-hidden border border-border"
                >
                  <img src={previewOf(url)} alt="Evidencia de entrega" className="w-full h-full object-cover" />
                  <PendingButton
                    pendingOn={deleteProofOfDelivery}
                    pendingKey={url}
                    variant="subtle"
                    size="xs"
                    icon={X}
                    iconOnly
                    pill
                    onClick={() => handleRemove(url)}
                    aria-label="Quitar imagen"
                    className="absolute top-1 right-1 !w-5 !h-5 bg-black/60 !text-white hover:!bg-danger"
                  />
                </div>
              ))}

              {proofImages.length < MAX_PROOF_IMAGES && (
                <label
                  className={`w-20 h-20 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 text-text-muted transition-colors ${uploadingProof
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer hover:border-ninja-green hover:text-ninja-green'
                    }`}
                >
                  <ImagePlus size={18} />
                  <span className="text-[10px] font-bold">Agregar</span>
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/png, image/jpeg"
                    onChange={handleFiles}
                    disabled={uploadingProof}
                  />
                </label>
              )}
            </div>

            {(fileError || proofError) && (
              <p className="text-danger text-xs mt-2">{fileError || proofError}</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-border">
          <Button
            variant="subtle"
            pill
            onClick={handleClose}
            disabled={busy}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            pill
            onClick={() => setConfirming(true)}
            disabled={!canSave}
        >
            Guardar Estado
          </Button>
        </div>
      </Modal>

      {/* The change cascades onto every bulto of the order and cannot be undone. */}
      <ConfirmDialog
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={handleSave}
        loading={saving}
        title="Actualizar el estado de toda la orden"
        message={`El estado se aplicará también a ${itemsCount === 1 ? 'el bulto asociado' : `los ${itemsCount} bultos asociados`}. Esta acción no se puede deshacer.`}
        confirmLabel="Actualizar estado"
        loadingLabel="Actualizando..."
      />
    </>
  );
};

export default EditStatusModal;
